import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import HeaderFilterMenu from "@/estimating/components/HeaderFilterMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ChevronsUpDown, ArrowUp, ArrowDown, FileText, Plus, Search, RotateCcw, Trash2, Lock, Unlock, Hash } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatMonthYear, calcCloseDate, formatCurrency, formatPercent } from "@/lib/formatters";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import NoteChips from "@/components/NoteChips";
import PersonPick from "@/components/PersonPick";
import MonthPicker from "@/components/MonthPicker";
import DirectoryContactPicker from "@/components/DirectoryContactPicker";
import { ContactCell } from "@/components/ContactCell";

const STATUS_OPTIONS = [
  "Invitation", "Estimating", "Estimate Complete", "Bid Submitted",
  "Budget Submitted", "Negotiating", "Won", "Lost", "Cancelled", "No Bid",
];

/** Parse "Name, Phone, Email" contact string into parts */
function parseContact(contactStr) {
  if (!contactStr) return { name: "", phone: "", email: "" };
  const parts = contactStr.split(",").map((s) => s.trim());
  let name = "", phone = "", email = "";
  for (const p of parts) {
    if (p.includes("@")) email = p;
    else if (/[\d\(\)\-\+]{7,}/.test(p.replace(/\s/g, ""))) phone = p;
    else if (!name) name = p;
  }
  return { name, phone, email };
}

const FILTER_SEP = "|";
// Auto close month = bid date + 4 months (rolls a month when past the 15th), as YYYY-MM-01.
const closeIso = (due) => {
  if (!due) return "";
  const d = new Date(due + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setMonth(d.getMonth() + 4);
  let m = d.getMonth(), y = d.getFullYear();
  if (d.getDate() > 15) { m += 1; if (m > 11) { m = 0; y += 1; } }
  return `${y}-${String(m + 1).padStart(2, "0")}-01`;
};
// Column header that sorts and filters from its own menu (Bid Board style).
function FilterHeader({ columnId, label, sortState, onSortChange, options, value, onChange, multi, menuOpen, setMenuOpen, searchable }) {
  // Label click sorts; the small filter icon opens the value menu.
  return (
    <HeaderFilterMenu label={label} testid={`pipeline-${columnId}`} multi={multi} separator={FILTER_SEP}
                      options={options} value={value} onChange={onChange}
                      open={menuOpen === columnId} onOpenChange={(o) => setMenuOpen(o ? columnId : null)}
                      onSort={(dir) => onSortChange({ field: columnId, dir })}
                      sortActive={sortState.field === columnId} direction={sortState.dir} sortOnLabel searchable={searchable} />
  );
}

// Same header button as Basis of Design: label + faint ⇅, arrow when active.
function SortHeader({ columnId, label, sortState, onSortChange }) {
  const on = sortState.field === columnId;
  const Icon = !on ? ChevronsUpDown : sortState.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button type="button" onClick={() => onSortChange({ field: columnId, dir: on && sortState.dir === "asc" ? "desc" : "asc" })}
            className={cn("inline-flex items-center gap-1 hover:text-foreground", on && "text-foreground")}
            data-testid={`sort-header-${columnId}`} data-sort={on ? sortState.dir : ""}>
      {label}<Icon className={cn("h-3 w-3", !on && "opacity-40")} />
    </button>
  );
}

/**
 * Row highlight rules (priority order):
 * 1. Red: Status = "Bid Submitted" AND Due Date > 60 days ago
 * 2. Yellow: Probability > 49%
 * 3. Green: Status = "Negotiating"
 */
function getRowHighlight(project) {
  if (project.archived) return "bg-slate-100/80";
  if (project.status === "Negotiating") return "bg-green-100/90";
  if (project.status === "Bid Submitted" && project.due_date) {
    const dueDate = new Date(project.due_date + "T00:00:00");
    const now = new Date();
    const diffDays = (now - dueDate) / (1000 * 60 * 60 * 24);
    if (diffDays > 60) return "bg-red-200/90";
  }
  if ((project.probability ?? 20) > 49) return "bg-yellow-50/80";
  return "";
}

export function PipelineTable({ estFilter, onEstFilterChange, customerFilter = [], onCustomerFilterChange, allEsts = [], allCustomers = [], data, onUpdateProject, onBatchUpdateStatus, onBatchArchive, isArchiveView, onUnarchive, onDeleteProject, sortState, onSortChange, onRefreshProjects, onLoadMore, loadingMore, totalCount, onLocalUpdate }) {
  const [editingCell, setEditingCell] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [unlockingIds, setUnlockingIds] = useState(new Set());
  const { isAdmin } = useAuth();
  const scrollRef = useRef(null);

  // Clear selection when row count changes (e.g. filter change re-fetches different result set).
  // Use length, not reference identity — fetches return new array refs even when rows are identical.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [data.length]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === data.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(data.map(p => p.id)));
  }, [data, selectedIds.size]);

  const saveContact = useCallback((project, d) => onUpdateProject(project.id, {
    customer_contact: [d.name, d.phone, d.email].filter(Boolean).join(", "), contact_name: d.name, contact_email: d.email,
  }), [onUpdateProject]);

  const handleInlineUpdate = useCallback(
    (projectId, field, value) => {
      onUpdateProject(projectId, { [field]: value });
      setEditingCell(null);
    },
    [onUpdateProject]
  );

  const handleOverwriteUpdate = useCallback(
    async (projectId, field, rawValue) => {
      setEditingCell(null);
      const clean = rawValue == null ? "" : String(rawValue).replace(/[^0-9.-]/g, "");
      const numVal = clean === "" ? null : parseFloat(clean);
      const isClear = numVal === null || isNaN(numVal);

      const payload = {};
      if (field === "overwritten_sales") {
        payload.clear_sales = isClear;
        if (!isClear) payload.overwritten_sales = numVal;
      } else {
        payload.clear_forecast = isClear;
        if (!isClear) payload.overwritten_forecast = numVal;
      }

      try {
        const updated = await api.updateProjectOverwrites(projectId, payload);
        if (onLocalUpdate) onLocalUpdate(projectId, updated);
      } catch (e) {
        console.error("Failed to update overwrite:", e);
        toast.error("Failed to save overwrite");
      }
    },
    [onLocalUpdate]
  );

  const handleUnlockProject = useCallback(async (projectId) => {
    setUnlockingIds(prev => new Set(prev).add(projectId));
    try {
      await api.unlockProject(projectId);
      if (onLocalUpdate) onLocalUpdate(projectId, { imported_archive: false });
      toast.success("Project unlocked for editing");
    } catch (e) {
      console.error("Failed to unlock project:", e);
      toast.error("Failed to unlock project");
    } finally {
      setUnlockingIds(prev => { const next = new Set(prev); next.delete(projectId); return next; });
    }
  }, [onLocalUpdate]);

  const handleAssignJobNumber = useCallback(async (project) => {
    try {
      const res = await api.assignJobNumber(project.id);
      if (onLocalUpdate) onLocalUpdate(project.id, { job_number: res.job_number });
      toast.success(`Job number ${res.job_number} assigned to ${project.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign job number");
    }
  }, [onLocalUpdate]);

  const EditableTextCell = useCallback(({ project, field, rowIndex, displayValue, placeholder }) => {
    const isReadOnly = project.imported_archive === true;
    const isEditing = !isReadOnly && editingCell?.rowId === project.id && editingCell?.field === field;

    if (isEditing) {
      return (
        <Input
          defaultValue={project[field] || ""}
          autoFocus
          className="h-7 text-xs w-full"
          data-testid={`${field}-input-${rowIndex}`}
          onBlur={(e) => handleInlineUpdate(project.id, field, e.target.value || "")}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleInlineUpdate(project.id, field, e.target.value || "");
            if (e.key === "Escape") setEditingCell(null);
          }}
        />
      );
    }

    return (
      <div
        className={cn(
          "text-xs text-slate-600 rounded px-1 py-0.5 -mx-1 transition-colors truncate",
          !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50"
        )}
        onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field })}
        data-testid={`cell-${field}-${rowIndex}`}
        title={displayValue || placeholder}
      >
        {displayValue || <span className="text-slate-400 italic">{placeholder}</span>}
      </div>
    );
  }, [editingCell, handleInlineUpdate]);

  // Derived outside columns memo so columns don't rebuild on every data change.
  const allSelected = useMemo(
    () => data.length > 0 && selectedIds.size === data.length,
    [data.length, selectedIds]
  );

  const columns = useMemo(
    () => [
      {
        id: "select",
        header: () => <span />,
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedIds.has(row.original.id)}
            onChange={() => toggleSelect(row.original.id)}
            className="h-3.5 w-3.5 rounded border-slate-300 accent-blue-600 cursor-pointer"
            data-testid={`select-row-${row.index}`}
          />
        ),
        size: 36,
      },
      {
        accessorKey: "name",
        header: () => <SortHeader columnId="name" label="Name" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isLocked = project.imported_archive === true;
          return (
            <div className="flex items-center gap-1.5" data-testid={`cell-name-${row.index}`} title={row.getValue("name")}>
              <span className="font-medium text-slate-900 truncate max-w-[180px]">
                {project.job_number != null && (
                  <span className="text-blue-700 tabular-nums" data-testid={`job-number-${row.index}`}>{project.job_number} — </span>
                )}
                {row.getValue("name")}
              </span>
              {isAdmin && project.job_number == null && (
                <button
                  onClick={() => handleAssignJobNumber(project)}
                  className="flex-shrink-0 text-slate-300 hover:text-blue-600 transition-colors"
                  title="Assign the next ABS job number (permanent)"
                  data-testid={`assign-job-number-${row.index}`}
                >
                  <Hash className="h-3 w-3" />
                </button>
              )}
              {isLocked && (
                <button
                  onClick={() => handleUnlockProject(project.id)}
                  disabled={unlockingIds.has(project.id)}
                  className="flex-shrink-0 text-slate-400 hover:text-blue-600 transition-colors"
                  title="This project is read-only (imported archive). Click to unlock for editing."
                  data-testid={`unlock-button-${row.index}`}
                >
                  <Lock className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        },
        size: 210,
      },
      {
        accessorKey: "customer_name",
        header: () => <FilterHeader columnId="customer_name" label="Customer" sortState={sortState} onSortChange={onSortChange} multi searchable menuOpen={menuOpen} setMenuOpen={setMenuOpen}
                                    options={allCustomers.map((c) => ({ value: c, label: c }))}
                                    value={customerFilter.join(FILTER_SEP)} onChange={(v) => onCustomerFilterChange(v ? v.split(FILTER_SEP) : [])} />,
        cell: ({ row }) => {
          const val = row.getValue("customer_name");
          const display = val && val.trim() && val.trim() !== "Not Assigned" ? val : "Not Assigned";
          return <EditableTextCell project={row.original} field="customer_name" rowIndex={row.index} displayValue={display} placeholder="Not Assigned" />;
        },
        size: 160,
      },
      {
        accessorKey: "account_manager",
        header: () => <SortHeader columnId="account_manager" label="AM" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          return <PersonPick value={project.account_manager} kind="am" readOnly={project.imported_archive === true}
                             testid={`cell-account-manager-${row.index}`} onChange={(v) => handleInlineUpdate(project.id, "account_manager", v)} />;
        
        },
        size: 60,
      },
      {
        accessorKey: "estimator",
        header: () => <FilterHeader columnId="estimator" label="EST" sortState={sortState} onSortChange={onSortChange} menuOpen={menuOpen} setMenuOpen={setMenuOpen}
                                    options={allEsts.map((e) => ({ value: e, label: e }))}
                                    value={estFilter === "ALL" ? "" : estFilter} onChange={(v) => onEstFilterChange(v || "ALL")} />,
        cell: ({ row }) => {
          const project = row.original;
          return <PersonPick value={project.estimator} kind="est" readOnly={project.imported_archive === true}
                             testid={`cell-estimator-${row.index}`} onChange={(v) => handleInlineUpdate(project.id, "estimator", v)} />;
        
        },
        size: 50,
      },
      {
        accessorKey: "status",
        header: () => <SortHeader columnId="status" label="Status" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          if (project.imported_archive) {
            return <div data-testid={`cell-status-${row.index}`}><StatusBadge status={project.status} /></div>;
          }
          return (
            <div data-testid={`cell-status-${row.index}`}>
              <Select value={project.status} onValueChange={(val) => handleInlineUpdate(project.id, "status", val)}>
                <SelectTrigger className="h-7 border-0 bg-transparent p-0 shadow-none focus:ring-0 w-auto" data-testid={`status-select-${row.index}`}>
                  <SelectValue><StatusBadge status={project.status} /></SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase().replace(/\s+/g, '-')}`}><StatusBadge status={s} /></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        },
        size: 140,
      },
      {
        accessorKey: "profit_margin",
        header: () => <SortHeader columnId="profit_margin" label="Profit %" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          const v = row.getValue("profit_margin");
          const isEditing = !isReadOnly && editingCell?.rowId === project.id && editingCell?.field === "profit_margin";
          const commit = (raw) => { const n = parseFloat(raw); handleInlineUpdate(project.id, "profit_margin", isNaN(n) ? null : Math.min(100, Math.max(0, n))); };
          if (isEditing) {
            return (
              <Input type="number" min="0" max="100" step="0.1" defaultValue={v ?? ""} autoFocus className="h-7 text-xs w-16 text-right"
                data-testid={`profit-margin-input-${row.index}`}
                onBlur={(e) => commit(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commit(e.target.value); if (e.key === "Escape") setEditingCell(null); }} />
            );
          }
          return (
            <div className={cn("text-right tabular-nums text-slate-700 font-medium rounded px-1 py-0.5 -mx-1 transition-colors",
                               !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50")}
                 data-testid={`cell-profit-margin-${row.index}`}
                 onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "profit_margin" })}
                 title={isReadOnly ? undefined : "Click to edit — a re-pushed estimate replaces it"}>
              {v == null || isNaN(v) ? "--" : `${Math.round(v)}%`}
            </div>
          );
        },
        size: 80,
      },
      {
        accessorKey: "total_sales",
        header: () => <SortHeader columnId="total_sales" label="Total Sales" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          const hasOverwrite = project.overwritten_sales != null;
          const displayValue = hasOverwrite ? project.overwritten_sales : project.total_sales;
          const isEditing = !isReadOnly && editingCell?.rowId === project.id && editingCell?.field === "total_sales";
          const commit = (raw) => { const clean = String(raw ?? "").replace(/[^0-9.-]/g, ""); const n = parseFloat(clean); handleInlineUpdate(project.id, "total_sales", clean === "" || isNaN(n) ? null : n); };
          if (isEditing) {
            return (
              <Input type="text" inputMode="decimal" defaultValue={displayValue ?? ""} autoFocus className="h-7 text-xs w-28 text-right"
                data-testid={`total-sales-input-${row.index}`}
                onBlur={(e) => commit(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commit(e.target.value); if (e.key === "Escape") setEditingCell(null); }} />
            );
          }
          return (
            <div className={cn("text-right tabular-nums font-medium rounded px-1 py-0.5 -mx-1 transition-colors", hasOverwrite ? "text-blue-900" : "text-slate-700",
                               !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50")}
                 data-testid={`cell-total-sales-${row.index}`}
                 onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "total_sales" })}
                 title={isReadOnly ? undefined : hasOverwrite ? `Earlier override (original: ${formatCurrency(project.total_sales)}) — click to replace` : "Click to edit — a re-pushed estimate replaces it; Forecast recalculates"}>
              {formatCurrency(displayValue)}
            </div>
          );
        },
        size: 110,
      },
      {
        accessorKey: "due_date",
        header: () => <SortHeader columnId="due_date" label="Bid Date" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          const isEditing = !isReadOnly && editingCell?.rowId === project.id && editingCell?.field === "due_date";
          if (isEditing) {
            return (
              <Input type="date" defaultValue={project.due_date || ""} autoFocus className="h-7 text-xs w-32" data-testid={`due-date-input-${row.index}`}
                onBlur={(e) => handleInlineUpdate(project.id, "due_date", e.target.value || null)}
                onKeyDown={(e) => { if (e.key === "Enter") handleInlineUpdate(project.id, "due_date", e.target.value || null); if (e.key === "Escape") setEditingCell(null); }}
              />
            );
          }
          const val = project.due_date;
          if (!val) {
            return (
              <div className={cn("text-slate-400 italic text-xs rounded px-1 py-0.5 -mx-1", !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50")}
                onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "due_date" })} data-testid={`cell-due-date-${row.index}`}>
                {isReadOnly ? "--" : "Set date"}
              </div>
            );
          }
          const d = new Date(val + "T00:00:00");
          return (
            <div className={cn("text-slate-600 text-xs rounded px-1 py-0.5 -mx-1", !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50")}
              onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "due_date" })} data-testid={`cell-due-date-${row.index}`}>
              {d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          );
        },
        size: 130,
      },
      {
        accessorKey: "completion",
        header: () => <SortHeader columnId="completion" label="Completion" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          
          // Display as "May 2027" format
          const displayVal = project.completion ? (() => {
            const d = new Date(project.completion + "T00:00:00");
            if (isNaN(d.getTime())) return null;
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            return `${months[d.getMonth()]} ${d.getFullYear()}`;
          })() : null;
          
          return (
            <MonthPicker value={project.completion} disabled={isReadOnly} testid={`cell-completion-${row.index}`}
                         onChange={(v) => handleInlineUpdate(project.id, "completion", v)}>
              <span className="whitespace-nowrap text-xs text-slate-600">{displayVal || <span className="text-slate-400 italic">{isReadOnly ? "--" : "Set date"}</span>}</span>
            </MonthPicker>
          );
        },
        size: 125,
      },
      {
        accessorKey: "close",
        header: () => <span>Close</span>,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          const hasOverwrite = !!project.overwritten_close;
          
          // Display value: overwrite takes priority, otherwise default calc
          const displayVal = hasOverwrite ? (() => {
            const d = new Date(project.overwritten_close + "T00:00:00");
            if (isNaN(d.getTime())) return null;
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            return `${months[d.getMonth()]} ${d.getFullYear()}`;
          })() : calcCloseDate(project.due_date);
          
          const handleCloseSave = async (parsed) => {
            const payload = { overwritten_close: parsed };
            try {
              const updated = await api.updateProjectOverwrites(project.id, payload);
              if (onLocalUpdate) onLocalUpdate(project.id, updated);
            } catch (e) {
              console.error("Failed to update close date:", e);
              toast.error("Failed to save close date");
            }
          };
          
          return (
            <MonthPicker value={project.overwritten_close || closeIso(project.due_date)} disabled={isReadOnly} testid={`cell-close-${row.index}`}
                         onChange={handleCloseSave}>
              <span className={cn("whitespace-nowrap text-xs", hasOverwrite ? "text-blue-600 font-medium" : "text-slate-600")}>
                {displayVal || <span className={cn("italic", isReadOnly ? "text-slate-400" : "text-blue-400 underline decoration-dotted")}>{isReadOnly ? "--" : "Set date"}</span>}
              </span>
            </MonthPicker>
          );
        },
        sortingFn: (rowA, rowB) => (rowA.original.due_date || "").localeCompare(rowB.original.due_date || ""),
        size: 125,
      },
      {
        accessorKey: "probability",
        header: () => <SortHeader columnId="probability" label="Prob %" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          const isEditing = !isReadOnly && editingCell?.rowId === project.id && editingCell?.field === "probability";
          if (isEditing) {
            return (
              <Input type="number" min="0" max="100" defaultValue={project.probability ?? 20} autoFocus className="h-7 text-xs w-16 text-right" data-testid={`probability-input-${row.index}`}
                onBlur={(e) => { const v = parseFloat(e.target.value); handleInlineUpdate(project.id, "probability", isNaN(v) ? 20 : Math.min(100, Math.max(0, v))); }}
                onKeyDown={(e) => { if (e.key === "Enter") { const v = parseFloat(e.target.value); handleInlineUpdate(project.id, "probability", isNaN(v) ? 20 : Math.min(100, Math.max(0, v))); } if (e.key === "Escape") setEditingCell(null); }}
              />
            );
          }
          return (
            <div className={cn("text-right tabular-nums font-medium text-slate-700 rounded px-1 py-0.5 -mx-1", !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50")}
              onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "probability" })} data-testid={`cell-probability-${row.index}`}>
              {project.probability ?? 20}%
            </div>
          );
        },
        size: 58,
      },
      {
        accessorKey: "forecasted_revenue",
        header: () => <SortHeader columnId="forecasted_revenue" label="Frcst Rev" sortState={sortState} onSortChange={onSortChange} />,
        cell: ({ row }) => {
          const project = row.original;
          const isReadOnly = project.imported_archive === true;
          const isEditing = !isReadOnly && editingCell?.rowId === project.id && editingCell?.field === "overwritten_forecast";
          const hasOverwrite = project.overwritten_forecast != null;
          const displayValue = project.forecasted_revenue;

          if (isEditing) {
            return (
              <Input
                type="text"
                inputMode="decimal"
                defaultValue={displayValue ?? ""}
                autoFocus
                className="h-7 text-xs w-28 text-right"
                placeholder="Clear to reset"
                data-testid={`forecast-rev-input-${row.index}`}
                onBlur={(e) => handleOverwriteUpdate(project.id, "overwritten_forecast", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleOverwriteUpdate(project.id, "overwritten_forecast", e.target.value);
                  if (e.key === "Escape") setEditingCell(null);
                }}
              />
            );
          }
          return (
            <div
              className={cn(
                "text-right tabular-nums font-medium rounded px-1 py-0.5 -mx-1 transition-colors",
                hasOverwrite ? "text-blue-900" : "text-slate-700",
                !isReadOnly && "cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50"
              )}
              onClick={isReadOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "overwritten_forecast" })}
              data-testid={`cell-forecasted-revenue-${row.index}`}
              title={hasOverwrite ? "Typed forecast (clear the input to go back to Total Sales × Prob %; editing Total Sales or Prob % also resets it)" : "Total Sales × Prob % — click to type a figure"}
            >
              {formatCurrency(displayValue)}
              {hasOverwrite && <span className="text-[9px] text-blue-400 ml-0.5">*</span>}
            </div>
          );
        },
        size: 110,
      },
      // NOTES — inline "MM/YY text" chips, oldest left → newest right; a legacy free-text note is the first chip
      {
        accessorKey: "notes",
        header: () => <span>Notes</span>,
        cell: ({ row }) => {
          const project = row.original;
          const notes = [...(project.notes ? [{ text: project.notes, user: "", initials: "", at: null }] : []), ...(project.note_log || [])];
          return (
            <NoteChips notes={notes} testid={`pipeline-notes-${project.id}`} target={{ project_id: project.id }}
                       canAdd={project.imported_archive !== true}
                       onAdd={async (text) => {
                         try { const updated = await api.addProjectNote(project.id, text); onLocalUpdate?.(project.id, updated); return true; }
                         catch (e) { toast.error(e?.response?.data?.detail || "Note not saved"); return false; }
                       }} />
          );
        },
        size: 360,
      },
      // CONTACT — name over email; "+" searches the Directory (row's customer first) or adds a new contact to it
      {
        id: "contact_name",
        accessorFn: (row) => parseContact(row.customer_contact).name,
        header: () => "Contact",
        cell: ({ row }) => {
          const project = row.original;
          const c = parseContact(project.customer_contact);
          const name = c.name || project.contact_name || "";
          const email = c.email || project.contact_email || "";
          const readOnly = project.imported_archive === true;
          const save = (d) => saveContact(project, { name, email, phone: c.phone, ...d });
          const picker = (trigger) => (
            <DirectoryContactPicker companyName={project.customer_name || ""} testid={`cell-contact-${row.index}-dir`} onPick={save}>{trigger}</DirectoryContactPicker>
          );
          if (!name && !email) {
            return readOnly ? <span className="text-slate-400">--</span> : picker(
              <button type="button" title="Add contact from the Directory" data-testid={`cell-contact-${row.index}-add`}
                      className="flex w-full min-w-[6rem] items-center gap-1 py-1 text-left text-muted-foreground hover:text-[#007BFF]"><Plus className="h-3.5 w-3.5" /></button>);
          }
          return (
            <div className="group/ct flex items-start gap-1">
              <ContactCell name={name} email={email} readOnly={readOnly} testid={`cell-contact-${row.index}`}
                           directory={{ companyName: project.customer_name || "", type: "gc" }} onSave={save} />
              {!readOnly && picker(
                <button type="button" title="Pick from the Directory" data-testid={`cell-contact-${row.index}-pick`}
                        className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 opacity-0 transition-opacity hover:text-[#007BFF] group-hover/ct:opacity-100"><Search className="h-3 w-3" /></button>)}
            </div>
          );
        },
        size: 190,
      },
      // PHONE — its own column (stored in customer_contact with name/email)
      {
        id: "contact_phone",
        accessorFn: (row) => parseContact(row.customer_contact).phone,
        header: () => <span>Phone</span>,
        cell: ({ row }) => {
          const project = row.original;
          const c = parseContact(project.customer_contact);
          const readOnly = project.imported_archive === true;
          const isEditing = !readOnly && editingCell?.rowId === project.id && editingCell?.field === "contact_phone";
          const commit = (v) => { setEditingCell(null); if (v.trim() !== c.phone) saveContact(project, { name: c.name || project.contact_name || "", email: c.email || project.contact_email || "", phone: v.trim() }); };
          if (isEditing) {
            return <input autoFocus defaultValue={c.phone} placeholder="Phone" type="tel" data-testid={`cell-contact-phone-input-${row.index}`}
                          className="w-28 border-0 border-b border-[#007BFF] bg-transparent py-0.5 text-xs outline-none"
                          onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingCell(null); }} />;
          }
          return (
            <div className={cn("min-h-[20px] text-xs", !readOnly && "cursor-text")} data-testid={`cell-contact-phone-${row.index}`}
                 onClick={readOnly ? undefined : () => setEditingCell({ rowId: project.id, field: "contact_phone" })}>
              {c.phone ? <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()} className="text-sky-700 hover:underline dark:text-sky-400">{c.phone}</a>
                       : <span className="text-muted-foreground">—</span>}
            </div>
          );
        },
        size: 120,
      },
      // RESTORE + DELETE - archive view only
      ...(isArchiveView ? [{
        id: "actions",
        header: () => <span>Actions</span>,
        cell: ({ row }) => {
          const project = row.original;
          return (
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-emerald-700 border-emerald-600 hover:bg-emerald-50 dark:text-emerald-400"
                onClick={() => onUnarchive(project.id)} data-testid={`restore-button-${row.index}`} title="Restore project">
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`delete-button-${row.index}`} title="Permanently delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to permanently delete this project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      "{project.name}" will be permanently removed. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`delete-cancel-${row.index}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => onDeleteProject(project.id)}
                      data-testid={`delete-confirm-${row.index}`}
                    >
                      Delete permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          );
        },
        size: 90,
      }] : []),
    ],
    [editingCell, handleInlineUpdate, handleOverwriteUpdate, handleUnlockProject, unlockingIds, EditableTextCell, isArchiveView, onUnarchive, onDeleteProject, onBatchArchive, sortState, onSortChange, selectedIds, toggleSelect, toggleSelectAll, allSelected, menuOpen, estFilter, onEstFilterChange, customerFilter, onCustomerFilterChange, allEsts, allCustomers, onUpdateProject, onLocalUpdate, saveContact]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  // Infinite scroll — load more when near bottom
  const hasMore = data.length < (totalCount || 0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !onLoadMore) return;
    const handleScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200 && hasMore && !loadingMore) {
        onLoadMore();
      }
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [onLoadMore, hasMore, loadingMore]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto pipeline-scroll-container" data-testid="pipeline-table-container">
      {/* Batch action bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 bg-blue-600 text-white px-4 py-2 flex items-center gap-3 text-xs" data-testid="batch-action-bar">
          <span className="font-medium">{selectedIds.size} selected</span>
          <span className="text-blue-200">|</span>
          {!isArchiveView && (
            <>
              <span className="text-blue-100 text-xs">Set status:</span>
              <Select value="" onValueChange={async (s) => { if (onBatchUpdateStatus) { await onBatchUpdateStatus([...selectedIds], s); setSelectedIds(new Set()); } }}>
                <SelectTrigger className="h-7 w-44 border-white bg-white text-xs font-medium text-slate-900 focus:ring-0 dark:border-white dark:!bg-white dark:!text-slate-900 [&_svg]:text-slate-700" data-testid="batch-status-select">
                  <SelectValue placeholder="Choose status…" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`batch-status-${s.toLowerCase().replace(/\s+/g, "-")}`}><StatusBadge status={s} /></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs text-blue-200 hover:text-white underline"
            data-testid="batch-clear-selection"
          >
            Clear
          </button>
        </div>
      )}
      <div className="relative w-full">
        <table style={{ minWidth: 2200 }} className="w-full caption-bottom text-xs">
        <TableHeader className="sticky top-0 z-30 bg-slate-50">
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="border-b border-slate-200 hover:bg-transparent">
              {hg.headers.map((header, headerIdx) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    "h-auto bg-slate-50 text-[11px] font-semibold text-muted-foreground px-4 py-2 whitespace-nowrap",
                    headerIdx === 0 && "px-2.5",
                    headerIdx < 2 && "sticky z-40 overflow-hidden bg-clip-padding",
                    headerIdx === 1 && "shadow-[inset_-1px_0_0_hsl(var(--border))]"
                  )}
                  style={{
                    width: header.getSize(), ...(headerIdx < 2 ? { minWidth: header.getSize(), maxWidth: header.getSize() } : {}),
                    left: headerIdx === 0 ? 0 : headerIdx === 1 ? 36 : undefined,
                  }}
                  data-testid={`column-header-${header.id}`}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length > 0 ? (
            <>
              {virtualizer.getVirtualItems().length > 0 && (
                <tr style={{ height: virtualizer.getVirtualItems()[0]?.start || 0 }} />
              )}
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];
                const highlight = getRowHighlight(row.original);
                return (
                  <TableRow
                    key={row.id}
                    data-index={virtualRow.index}
                    className={cn(
                      "group border-b border-slate-100",
                      highlight || "hover:bg-slate-50/80"
                    )}
                    data-testid={`pipeline-row-${row.index}`}
                    data-mention-row={`pipeline-${row.original.id}`}
                  >
                    {row.getVisibleCells().map((cell, cellIdx) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "px-4 py-2.5 whitespace-nowrap",
                          cellIdx === 0 && "px-2.5",
                          cellIdx < 2 && "sticky z-10 overflow-hidden bg-white bg-clip-padding",
                          cellIdx === 1 && "shadow-[inset_-1px_0_0_hsl(var(--border))]"
                        )}
                        style={{
                          width: cell.column.getSize(), ...(cellIdx < 2 ? { minWidth: cell.column.getSize(), maxWidth: cell.column.getSize() } : {}),
                          left: cellIdx === 0 ? 0 : cellIdx === 1 ? 36 : undefined,
                        }}
                      >
                        {cellIdx < 2 && <span aria-hidden className={cn("pointer-events-none absolute inset-0", highlight, "group-hover:bg-slate-50/80")} />}
                        <div className={cellIdx < 2 ? "relative" : undefined}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {virtualizer.getVirtualItems().length > 0 && (
                <tr style={{ height: virtualizer.getTotalSize() - (virtualizer.getVirtualItems()[virtualizer.getVirtualItems().length - 1]?.end || 0) }} />
              )}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-48 text-center">
                <div className="flex flex-col items-center gap-3" data-testid="empty-state">
                  <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center">
                    <FileText className="h-7 w-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-600">
                      {isArchiveView ? "No archived projects" : "No projects match current filters"}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {isArchiveView
                        ? "Projects with Won, Lost, or Cancelled status will appear here"
                        : "Try adjusting your filters or import an Excel file"}
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
      </div>
      {loadingMore && (
        <div className="flex items-center justify-center py-3 border-t border-slate-100" data-testid="loading-more">
          <div className="h-5 w-5 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin mr-2" />
          <span className="text-xs text-slate-500">Loading more projects...</span>
        </div>
      )}
      {!loadingMore && hasMore && rows.length > 0 && (
        <div className="text-center py-2 text-xs text-slate-400" data-testid="scroll-hint">
          {data.length} of {totalCount} loaded — scroll for more
        </div>
      )}
    </div>
  );
}
